<template>
  <div class="embed-frame" :class="{ 'embed-frame--compact': compact }">
    <div v-if="title || subtitle" class="embed-frame__intro">
      <h2 v-if="title" class="font-sora embed-frame__title">{{ title }}</h2>
      <p v-if="subtitle" class="embed-frame__subtitle">{{ subtitle }}</p>
    </div>

    <!-- Third-party embed: only mounted once the visitor has consented, or
         has explicitly clicked to load it. Rendering it on sight would let a
         tour/booking provider set cookies before opt-in — the PECR gap the
         banner exists to close (same treatment as the demo video). -->
    <div v-if="src && showEmbed" class="embed-frame__box">
      <iframe
        :src="src"
        :title="iframeTitle"
        loading="lazy"
        allow="fullscreen; clipboard-write"
        allowfullscreen
        referrerpolicy="strict-origin-when-cross-origin"
      />
    </div>

    <div
      v-else-if="src"
      class="embed-frame__placeholder embed-frame__placeholder--clickable"
      role="button"
      tabindex="0"
      @click="loaded = true"
      @keyup.enter="loaded = true"
      @keyup.space="loaded = true"
    >
      <p class="embed-frame__placeholder-title">{{ placeholderTitle }}</p>
      <p class="embed-frame__placeholder-body">
        This is hosted by a third party that may set cookies. Select to load it.
      </p>
    </div>

    <div v-else class="embed-frame__placeholder">
      <p class="embed-frame__placeholder-title">{{ placeholderTitle }}</p>
      <p class="embed-frame__placeholder-body">{{ placeholderBody }}</p>
      <a
        v-if="fallbackHref"
        :href="fallbackHref"
        class="embed-frame__fallback"
        :target="fallbackExternal ? '_blank' : undefined"
        :rel="fallbackExternal ? 'noopener' : undefined"
      >
        {{ fallbackLabel }}
      </a>
    </div>
  </div>
</template>

<script>
import { eventBus } from '@/utils/eventBus';
import { hasMarketing } from '@/utils/consent';

export default {
  name: 'EmbedFrame',
  props: {
    src: { type: String, default: '' },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    iframeTitle: { type: String, default: 'IMPROVE embed' },
    placeholderTitle: {
      type: String,
      default: 'Coming soon',
    },
    placeholderBody: {
      type: String,
      default:
        'This experience will appear here once the booking or tour link is configured.',
    },
    fallbackHref: { type: String, default: '' },
    fallbackLabel: { type: String, default: 'Continue' },
    fallbackExternal: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      // set by an explicit click on the placeholder
      loaded: false,
      // hasMarketing() reads a cookie, which is not reactive; bumping this on
      // the consent-updated signal re-evaluates showEmbed so accepting in the
      // banner reveals the embed immediately instead of only after a reload.
      consentTick: 0,
    };
  },
  computed: {
    showEmbed() {
      return this.loaded || (this.consentTick >= 0 && hasMarketing());
    },
  },
  mounted() {
    eventBus.$on('consent-updated', this.onConsentUpdated);
  },
  beforeDestroy() {
    eventBus.$off('consent-updated', this.onConsentUpdated);
  },
  methods: {
    onConsentUpdated() {
      this.consentTick += 1;
    },
  },
};
</script>

<style scoped>
.embed-frame {
  width: 100%;
}

.embed-frame__intro {
  text-align: center;
  margin-bottom: 20px;
}

.embed-frame__title {
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
}

.embed-frame__subtitle {
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  margin: 0;
  line-height: 1.5;
}

.embed-frame__box {
  position: relative;
  width: 100%;
  min-height: 640px;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e6ebf1;
  box-shadow: 0 12px 32px rgba(0, 36, 88, 0.1);
}

.embed-frame__box iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.embed-frame__placeholder {
  text-align: center;
  background: #f7f9fb;
  border: 1px dashed #c5d0de;
  border-radius: 12px;
  padding: 48px 28px;
}

.embed-frame__placeholder-title {
  color: var(--brand-navy, #002458);
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 10px;
}

.embed-frame__placeholder-body {
  color: var(--brand-grey, #6b7280);
  font-size: 15px;
  line-height: 1.5;
  max-width: 480px;
  margin: 0 auto 20px;
}

.embed-frame__fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 22px;
  border-radius: 8px;
  background: var(--brand-navy, #002458);
  color: #fff;
  font-weight: 700;
  text-decoration: none;
}

.embed-frame__fallback:hover {
  background: var(--brand-teal, #08a0a8);
  color: #fff;
}

.embed-frame--compact .embed-frame__box {
  min-height: 520px;
}

@media screen and (max-width: 700px) {
  .embed-frame__box {
    min-height: 480px;
  }
}
.embed-frame__placeholder--clickable {
  cursor: pointer;
}

.embed-frame__placeholder--clickable:hover,
.embed-frame__placeholder--clickable:focus {
  border-color: var(--brand-teal, #08a0a8);
  outline: none;
}
</style>
